import { CITY } from '@city'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { PageMeta } from '../../seo/PageMeta'
import { DEFAULT_LOCALE, isSupportedLocale, type SupportedLocale } from '../../i18n/locales'
import { SiteUrls } from '../../../scripts/siteUrls.mjs'
import type { SchemaStation } from '../../schema/types'

interface Props {
  /** URL locale segment (raw route param — guarded before PageMeta). */
  locale: string
  station: SchemaStation
  /** Every line serving the station, display order (English names). */
  lineNames: readonly string[]
  /** Station-content address, when the record carries one. */
  address?: string
}

/**
 * SEO head of the station DETAIL page (`/:locale/station/<naptanId>`) —
 * the informational surface of the two-surface split (content spec §2):
 * title/description carry the GSC query intent (zone + lines + exits +
 * first/last trains), the canonical is ALWAYS the naptanId URL (the
 * route also accepts slug / schema id — those forms canonicalize here),
 * and the `TrainStation` JSON-LD is localized via `inLanguage` while
 * the proper nouns stay English. The map surface's navigational
 * formula lives with the map prerender, never here.
 *
 * The head is emitted only once the `station-page` catalog has arrived.
 * i18next runs with `useSuspense: false`, so before that `t()` returns
 * the key itself — and `PageMeta` both publishes what it is given AND
 * deletes the prerendered tags it replaces. A visitor (or Googlebot's
 * renderer) arriving on a cold i18n cache therefore saw
 * `<title>meta_title_zone</title>` where the prerendered file had the
 * right localized title, with nothing left to fall back to. Holding the
 * head back costs nothing: until `ready` the correct prerendered tags
 * are still in place, which is exactly what should be shown.
 */
export function StationPageMeta({ locale, station, lineNames, address }: Props): ReactElement {
  const { t, ready } = useTranslation('station-page')
  const supportedLocale: SupportedLocale = isSupportedLocale(locale) ? locale : DEFAULT_LOCALE
  const stationName = station.localName
  const zone = station.zone ?? undefined
  const naptanId = station.naptanId ?? station.id

  const title =
    zone !== undefined
      ? t('meta_title_zone', { station: stationName, zone })
      : t('meta_title', { station: stationName })
  const description =
    zone !== undefined
      ? t('meta_description_zone', { station: stationName, zone })
      : t('meta_description', { station: stationName })

  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'TrainStation',
    name: `${stationName} Underground Station`,
    inLanguage: supportedLocale,
    ...(address !== undefined && { address }),
    ...(zone !== undefined && {
      additionalProperty: {
        '@type': 'PropertyValue',
        name: 'Fare zone',
        value: zone,
      },
    }),
    ...(lineNames.length > 0 && {
      containedInPlace: {
        '@type': 'City',
        name: CITY.locality,
      },
      // The serving lines, English names (cross-platform proper nouns).
      isPartOf: lineNames.map((name) => ({ '@type': 'TrainLine', name: `${name} line` })),
    }),
  }

  return (
    <>
      {ready ? (
        <PageMeta
          locale={supportedLocale}
          path={`/station/${naptanId}`}
          title={title}
          description={description}
          // The detail surface is prerendered for DETAIL_LOCALES only;
          // every other locale prefix 301s onto one of them (nginx,
          // TASK-1206). The cluster has to name the 15 that exist — see
          // PageMeta's `hreflangLocales`.
          hreflangLocales={SiteUrls.DETAIL_LOCALES}
          // The prerendered head types this surface as a `place`; the
          // runtime emitter is now the only one, so it must agree.
          ogType="place"
        />
      ) : null}
      {/* In-body JSON-LD, same mechanism as the FAQ section's FAQPage
          script — react-helmet-async silently drops <script> children
          during its head merge (observed 2026-08-05), and crawlers
          accept structured data anywhere in the document. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </>
  )
}
